import React, { useState, useEffect } from 'react';
import supabase from '../../../supabaseClient';
import { Calendar as BigCalendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pl';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

moment.locale('pl');
const localizer = momentLocalizer(moment);

const EmployeeScheduleView = () => {
  const [userID, setUserID] = useState('');
  const [idConfig, setIdConfiguration] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employees, setEmployees] = useState([]);

  const [currentDate, setCurrentDate] = useState(new Date());
  // Domyślny widok ustawiony na DZIEŃ
  const [currentView, setCurrentView] = useState(Views.DAY);

  const polishMessages = {
    date: "Data",
    time: "Czas",
    event: "Wydarzenie",
    allDay: "Cały dzień",
    week: "Tydzień",
    work_week: "Tydzień roboczy",
    day: "Dzień",
    month: "Miesiąc",
    previous: "Wstecz",
    next: "Dalej",
    yesterday: "Wczoraj",
    tomorrow: "Jutro",
    today: "Dzisiaj",
    agenda: "Agenda",
    noEventsInRange: "Brak wydarzeń w tym zakresie.",
    showMore: (total) => `+${total} więcej`
  };

  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        setUserID(data.session.user.id);
        fetchConfiguration(data.session.user.id);
      }
    };
    checkSession();
  }, []);

  const fetchConfiguration = async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id_configuration')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Błąd pobierania konfiguracji profilu:', error);
    } else if (data) {
      setIdConfiguration(data.id_configuration);
    }
  };

  const handleFetchEmployees = async (configId) => {
    const { data, error } = await supabase
      .from('employees')
      .select('id, name, surname')
      .eq('id_configuration', configId)
      .is('archived', null);

    if (error) {
      console.error('Błąd pobierania pracowników:', error);
    } else if (data) {
      setEmployees(data);
    }
  };

  const handleEmployeeChange = (event) => {
    setSelectedEmployee(event.target.value);
  };

  const fetchEvents = async (configId, employeeList, employeeId = '') => {
    if (!employeeList || employeeList.length === 0) {
      setEvents([]);
      return;
    }

    const targetEmployeeIds = employeeId 
      ? [employeeId] 
      : employeeList.map(e => e.id);

    let attendanceQuery = supabase
      .from('attendance')
      .select('id, employee_id, check_in, check_out, type')
      .in('employee_id', targetEmployeeIds)
      .eq('type', 'work');

    let absencesQuery = supabase
      .from('absences')
      .select('id, employee_id, absence_start, absence_end, type')
      .in('employee_id', targetEmployeeIds);

    const [
      { data: attendanceData, error: attendanceError },
      { data: absencesData, error: absencesError }
    ] = await Promise.all([attendanceQuery, absencesQuery]);

    if (attendanceError || absencesError) {
      console.error('Błąd Supabase:', attendanceError || absencesError);
      return;
    }

    const empMap = new Map(employeeList.map(e => [e.id, `${e.name} ${e.surname}`]));

    // Mapowanie pracy – ikona + imię i nazwisko
    const workEvents = (attendanceData || []).map((work) => {
      const empName = empMap.get(work.employee_id) || "Pracownik";
      const startDate = new Date(work.check_in);
      const endDate = work.check_out ? new Date(work.check_out) : new Date();

      return {
        id: `work_${work.id}`,
        title: `💼 ${empName}`,
        tooltip: `Praca: ${empName} (${moment(startDate).format('HH:mm')} - ${moment(endDate).format('HH:mm')})`,
        start: startDate,
        end: endDate,
        allDay: false,
        status: 'work',
        type: 'attendance'
      };
    });

    // Mapowanie urlopów / nieobecności
    const absenceEvents = (absencesData || []).map((abs) => {
      const empName = empMap.get(abs.employee_id) || "Pracownik";
      const absType = abs.type || 'urlop';

      return {
        id: `abs_${abs.id}`,
        title: `🏖️ ${empName}`,
        tooltip: `Nieobecność (${absType}): ${empName}`,
        start: moment(abs.absence_start).startOf('day').toDate(),
        end: moment(abs.absence_end).endOf('day').toDate(),
        allDay: true,
        status: absType,
        type: 'absence'
      };
    });

    setEvents([...workEvents, ...absenceEvents]);
  };

  useEffect(() => {
    if (idConfig) {
      handleFetchEmployees(idConfig);
    }
  }, [idConfig]);

  useEffect(() => {
    if (idConfig && employees.length > 0) {
      fetchEvents(idConfig, employees, selectedEmployee);
    }
  }, [idConfig, employees, selectedEmployee]);

  const eventStyleGetter = (event) => {
    let backgroundColor = '#888888';

    switch ((event.status || '').toLowerCase()) {
      case 'work':
        backgroundColor = '#2E7D32';
        break;
      case 'vacation':
      case 'urlop':
        backgroundColor = '#0288D1';
        break;
      case 'sickleave':
      case 'zwolnienie':
        backgroundColor = '#7B1FA2';
        break;
      default:
        backgroundColor = '#E65100';
    }

    return {
      style: {
        backgroundColor,
        color: '#ffffff',
        fontWeight: '600',
        fontSize: '13px',
        borderRadius: '6px',
        border: 'none',
        display: 'block',
        padding: '2px 5px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
      },
    };
  };

  const minTime = moment().hours(6).minutes(0).seconds(0).toDate();
  const maxTime = moment().hours(23).minutes(0).seconds(0).toDate();

  return (
    <div>
      <div style={{ marginTop: '20px', marginBottom: '20px', marginLeft: '20px' }}>
        <FormControl style={{ minWidth: '250px' }}>
          <InputLabel id="employee-select-label">
            Wybierz pracownika
          </InputLabel>
          <Select
            labelId="employee-select-label"
            label="Wybierz pracownika"
            value={selectedEmployee}
            onChange={handleEmployeeChange}
          >
            <MenuItem value="">Wszyscy pracownicy</MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {`${emp.name} ${emp.surname}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div style={{ height: '800px', padding: '0 20px' }}>
        <BigCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          eventPropGetter={eventStyleGetter}
          tooltipAccessor="tooltip"
          min={minTime}
          max={maxTime}
          messages={polishMessages}
          culture="pl"
          date={currentDate}
          onNavigate={(newDate) => setCurrentDate(newDate)}
          view={currentView}
          onView={(newView) => setCurrentView(newView)}
        />
      </div>
    </div>
  );
};

export default EmployeeScheduleView;